import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LogRocket from 'logrocket';
import { TapToPayProvider } from './TapToPayContext';
import { StartTapToPayFlowButton } from './StartTapToPayFlowButton';
import {
  ProAppointment,
  ProAppointmentDetails,
  getProAppointmentDetails,
} from '../../../../api/Providers/Appointments';
import TapToPayFlow from './TapToPayFlow';
import { HAD_SUCCESSFUL_CONNECTION_USERSTATE_KEY } from './constants';
import { PrivilegedProvider } from '../../../../api/Providers';
import { IRootDispatch, RootState } from '../../../../store/models';
import { isWithinCheckoutWindow } from '../../../../modules/provider/appointmentCheckout';
import analytics from '../../../../modules/analytics';
import nonCriticalException from '../../../../modules/exceptionLogger';
import store from '../../../../store';
import {
  isEligibleForTapToPay,
  isSimulated,
  TapToPayGeolocationAuthorizationStatus,
  TapToPayPlugin,
} from '../../../../modules/TapToPay';
import {
  useLinkTo,
  useOnce,
  useUserStateRedux,
} from '../../../../hooks';
import { ButtonAppearance, Stack } from '../../../ui';
import { PRO_TAPTOPAY_ONBOARD_SETTINGS_DEEP_LINK } from '../../../../route-names';
import { OptInSubtext } from './OptInSubtext';
import { GeolocationAuthorizationSubtext } from './GeolocationAuthorizationSubtext';

export type TapToPayFlowContainerProps = {
  appointment: ProAppointment;
  provider: PrivilegedProvider,
  amount: number,
  onUseDifferentPaymentMethodClick: () => void,
  subtextAppearance?: 'small' | 'medium',
  buttonAppearance?: ButtonAppearance,
  showTotalOnButton?: boolean,
  onCheckoutStart?: () => void,
  onCheckoutSuccess?: () => void,
};

export enum SubtextEvent {
  OptInLinkClicked = 'Checkout Tap2Pay Subtext Opt-In Link Clicked',
  GeolocationAuthorizationLinkClicked = 'Checkout Tap2Pay Subtext Geolocation Link Clicked',
}

export const TapToPayFlowContainer: React.FC<TapToPayFlowContainerProps> = ({
  appointment,
  provider,
  amount,
  onUseDifferentPaymentMethodClick,
  subtextAppearance = 'medium',
  buttonAppearance = 'outline-thin',
  showTotalOnButton = true,
  onCheckoutStart = () => {},
  onCheckoutSuccess = () => {},
}) => {
  const linkTo = useLinkTo({ replace: true });
  const dispatch = useDispatch<IRootDispatch>();
  const isPluginLoaded = useSelector<RootState, boolean>(
    store.select.stripeTerminal.isPluginLoaded,
  );
  const connectionProgress = useSelector<RootState, number>(
    store.select.stripeTerminal.connectionProgress,
  );
  const geolocationAuthorizationStatus = useSelector<RootState,
  TapToPayGeolocationAuthorizationStatus>(
    store.select.stripeTerminal.geolocationAuthorizationStatus,
  );
  const isLocationDeniedOrRestricted = (
    geolocationAuthorizationStatus === TapToPayGeolocationAuthorizationStatus.Denied
    || geolocationAuthorizationStatus === TapToPayGeolocationAuthorizationStatus.Restricted
  );
  const withinCheckoutWindow = isWithinCheckoutWindow(appointment);

  const [
    hadSuccessfulConnection,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _unused,
    hadSuccessfulConnectionLoaded,
  ] = useUserStateRedux<boolean>(HAD_SUCCESSFUL_CONNECTION_USERSTATE_KEY);

  const isEligible = useMemo(() => isEligibleForTapToPay(appointment, provider, amount), [
    provider,
    appointment,
    amount,
  ]);

  const notOptedIn = useMemo(
    () => hadSuccessfulConnectionLoaded && !hadSuccessfulConnection,
    [
      hadSuccessfulConnectionLoaded,
      hadSuccessfulConnection,
    ],
  );

  const refreshAppointment = useCallback(async (): Promise<void> => {
    try {
      const updatedAppt: ProAppointmentDetails = await getProAppointmentDetails(
        provider.id, appointment.id,
      );
      dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });
    } catch (e) {
      nonCriticalException(e);
    }
  }, [
    provider,
    appointment,
    dispatch.providerManageAppointment,
  ]);

  const handleCheckoutSuccess = useCallback(async (): Promise<void> => {
    await refreshAppointment();
    onCheckoutSuccess();
    // Show Checkout Success screen
  }, [
    refreshAppointment,
    onCheckoutSuccess,
  ]);

  const handleCheckoutFailure = useCallback(async (): Promise<void> => {
    await refreshAppointment();
  }, [refreshAppointment]);

  const startTapToPayButtonDisabled = (!isSimulated() && !isPluginLoaded)
    || !withinCheckoutWindow
    || notOptedIn
    || isLocationDeniedOrRestricted;

  useOnce(() => {
    let disabledReason = null;
    if (!isPluginLoaded && !isSimulated()) {
      disabledReason = 'plugin_not_loaded';
    } else if (!withinCheckoutWindow) {
      disabledReason = 'outside_checkout_window';
    } else if (notOptedIn) {
      disabledReason = 'not_setup';
    } else if (isLocationDeniedOrRestricted) {
      disabledReason = 'geolocation_denied';
    }
    analytics.track(
      'pro_tap_to_pay_flow_start_button_viewed',
      {
        client_id: appointment.client.id,
        provider_id: appointment.provider,
        appt_id: appointment.id,
        disabled: startTapToPayButtonDisabled,
        has_card_on_file: appointment.client_has_card,
        disabled_reason: disabledReason,
      },
    );

    LogRocket.track('Tap to Pay Checkout Button Viewed', { disabled: startTapToPayButtonDisabled });
    if (notOptedIn) LogRocket.track('Tap to Pay Checkout Button Disabled Not Opted-In');
    if (isLocationDeniedOrRestricted) LogRocket.track('Tap to Pay Checkout Button Disabled Geolocation Denied');
  }, isEligible);

  useOnce(() => {
    analytics.track('pro_tap_to_pay_flow_start_button_suppressed', {
      client_id: appointment.client.id,
      provider_id: appointment.provider,
      appt_id: appointment.id,
    });
  }, !isEligible);

  const handleOptInLinkPress = React.useCallback(() => {
    analytics.track(SubtextEvent.OptInLinkClicked, {
      appt_id: appointment.id,
    });
    linkTo(PRO_TAPTOPAY_ONBOARD_SETTINGS_DEEP_LINK, {
      source: 'ttp-checkout-subtext',
    });
  }, [
    appointment,
    linkTo,
  ]);

  const handleGeolocationAuthorizationLinkPress = React.useCallback(() => {
    analytics.track(SubtextEvent.GeolocationAuthorizationLinkClicked, {
      appt_id: appointment.id,
    });
    TapToPayPlugin.openSettingsApp();
  }, [appointment]);

  return !isEligible ? null : (
    <TapToPayProvider
      providerId={provider.id}
      isSimulated={isSimulated()}
    >
      <Stack space={16} paddingBottom={16}>
        <StartTapToPayFlowButton
          disabled={startTapToPayButtonDisabled}
          total={amount}
          appearance={buttonAppearance}
          showTotal={showTotalOnButton}
          onClick={onCheckoutStart}
        />

        {notOptedIn && (
          <OptInSubtext onLinkPress={handleOptInLinkPress} />
        )}
        {!notOptedIn && isLocationDeniedOrRestricted && (
          <GeolocationAuthorizationSubtext
            onLinkPress={handleGeolocationAuthorizationLinkPress}
            appearance={subtextAppearance}
            textAlign={subtextAppearance === 'small' ? 'left' : undefined}
          />
        )}

      </Stack>

      {!startTapToPayButtonDisabled && (
        <TapToPayFlow
          amount={amount}
          appointment={appointment}
          provider={provider}
          connectionProgress={connectionProgress}
          onCheckoutSuccess={handleCheckoutSuccess}
          onCheckoutFailure={handleCheckoutFailure}
          onUseDifferentPaymentMethodClick={onUseDifferentPaymentMethodClick}
        />
      )}
    </TapToPayProvider>
  );
};

export default TapToPayFlowContainer;
